import {
  Alert,
  Button,
  CloseButton,
  Dialog,
  Portal,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import useThemeColors from "../../hooks/useThemeColors";

interface Props {
  handleDelete?: () => void;
  heading?: string;
  message?: string | React.ReactNode;
  customMessage?: React.ReactNode;
  trigger?: React.ReactNode;
  openDeleteDialog?: boolean;
  setOpenDeleteDialog?: (open: boolean) => void;
  isDeleting?: boolean;
}

export default function DeleteAlertDialogModal({
  handleDelete,
  heading,
  message,
  customMessage,
  trigger,
  openDeleteDialog,
  setOpenDeleteDialog,
  isDeleting,
}: Props) {
  const executeDelete = async () => {
    handleDelete?.(); // await the async delete
    setOpenDeleteDialog?.(false); // close the dialog after delete completes
  };
  const colors = useThemeColors();

  return (
    <>
      <Dialog.Root
        role="alertdialog"
        motionPreset="slide-in-bottom"
        placement="top"
        open={openDeleteDialog}
        onOpenChange={(e) => setOpenDeleteDialog?.(e.open)}
      >
        {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
        <Portal>
          <Dialog.Positioner>
            <Dialog.Backdrop
              bg="rgba(0, 0, 0, 0.5)"
              backdropFilter="blur(5px)"
            />
            <Dialog.Content maxW={{ base: "calc(100vw - 32px)", md: "xl" }}>
              <Dialog.Header>{heading || "Are you Sure?"}</Dialog.Header>
              <Dialog.CloseTrigger disabled={isDeleting} asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
              <Dialog.Body>
                {customMessage || (
                  <VStack align="start" gap={3}>
                    <Alert.Root
                      status="error"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Alert.Indicator />
                      <Alert.Title fontWeight="bold">
                        This action cannot be undone!
                      </Alert.Title>
                      <Alert.Description>
                        {message || "You are about to permanently delete data."}
                      </Alert.Description>
                    </Alert.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  colorPalette={colors.schoolAccent.primary}
                  variant="solid"
                  disabled={isDeleting}
                  onClick={() => setOpenDeleteDialog?.(false)}
                >
                  Cancel
                </Button>

                <Button
                  variant="surface"
                  colorPalette="red"
                  ml={3}
                  onClick={executeDelete}
                  loading={isDeleting}
                >
                  {isDeleting ? <Spinner /> : "Delete"}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
